import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * "Meine Begleitung" — the daily companion of the DNA & Health SPECIAL
 * (docs/globalhealth/TAEGLICHE_BEGLEITUNG_PLAN.md). Member API under
 * /api/health_companion (Api::HealthCompanionController): the backend stores,
 * a Sidekiq job talks to the model, the page polls the overview.
 *
 * Every write goes under the `companion` root key — the backend filters that
 * key from its request logs.
 */

export type CompanionEntitlementStatus = 'none' | 'minor_consent_required' | 'active' | 'expired';
export type ObservationKind =
  'weight' | 'blood_pressure' | 'heart_rate' | 'sleep_hours' | 'sleep_quality' | 'supplement' | 'hydrogen_water' |
  'food' | 'drink' | 'exercise' | 'wellbeing' | 'mood' | 'energy' | 'stress' | 'reflection' | 'symptom_note' | 'note' |
  'availability' | 'plan_review';
export type ObservationState = 'given' | 'skipped' | 'unclear';
export type DayState = 'empty' | 'partial' | 'preview' | 'pending' | 'draft' | 'confirmed';

export interface CompanionEntitlement {
  status: CompanionEntitlementStatus;
  starts_at: string | null;
  ends_at: string | null;
  days_left: number;
}

export interface CompanionGoal { kind: string; text: string; target_value?: number | string | null; unit?: string | null }
export interface CompanionNote { text: string; recorded_on?: string; source?: string }

export interface CompanionGuardian {
  id: number; name: string; email: string; relation: string | null;
  status: 'pending' | 'confirmed' | 'declined' | 'expired' | 'revoked';
  sent_at: string | null; confirmed_at: string | null; expires_at: string | null;
}

export interface CompanionProfile {
  status: 'draft' | 'confirmed';
  confirmed: boolean;
  onboarding_step: string;
  goals: CompanionGoal[];
  baseline: Record<string, unknown> | null;
  notes: CompanionNote[];
  daily_time_minutes: number | null;
  weekday_time_minutes: number | null;
  movement_options: string | null;
  restrictions: string | null;
  language: string;
  time_zone: string;
  weight_unit: 'kg' | 'lb';
  volume_unit: 'ml' | 'l' | 'fl_oz';
  height_cm: number | string | null;
  height_recorded_on: string | null;
  date_of_birth: string | null;
  age_verified: boolean;
  minor: boolean;
  ai_enabled: boolean;
  ai_disabled_at: string | null;
  consent_version: string | null;
  consented_at: string | null;
  confirmed_at: string | null;
  stale: boolean;
  guardian: CompanionGuardian | null;
  today: string;
}

export interface PlanStep { area: 'nutrition' | 'hydration' | 'movement' | 'recovery' | 'habit'; text: string }
export interface CompanionPlan {
  id: number; reference_date: string; status: 'draft' | 'confirmed' | 'superseded' | 'declined';
  summary: string | null; steps: PlanStep[]; rationale: string | null; question: string | null;
  confirmed_at: string | null; review: { done: number[]; notes: string; reviewed_at?: string } | null;
  superseded_by_id: number | null; created_at: string; provider: string | null;
}

/** One understood value in the editable preview (and the form payload). */
export interface PreviewItem {
  kind: ObservationKind;
  state: ObservationState;
  value?: number | string | null;
  value2?: number | string | null;
  unit?: string | null;
  text?: string | null;
  reference_date?: string;
  reference_day?: 'today' | 'yesterday' | 'day_before_yesterday' | 'last_night';
  details?: Record<string, unknown>;
  question?: string | null;
  correction_of?: string | null;
  scale_version?: string | null;
  measured_at?: string | null;
  session_key?: string | null;
}

export interface CompanionMessage {
  id: number; role: 'member' | 'companion';
  kind: 'checkin' | 'clarify' | 'plan_draft' | 'plan_confirmed' | 'safety' | 'chat' | 'status' | 'note';
  status: 'done' | 'pending' | 'failed' | 'superseded';
  content: string | null; created_at: string; reference_date: string | null; in_reply_to_id: number | null;
  plan_id: number | null; error_code: string | null; retryable: boolean;
  preview: PreviewItem[] | null; preview_status: 'none' | 'pending' | 'confirmed' | 'discarded';
  follow_up: 'plan' | null; plan: CompanionPlan | null; provider: string | null; client_ref: string | null;
}

export interface CompanionObservation {
  id: number; reference_date: string; kind: ObservationKind; state: ObservationState;
  value: number | string | null; value2: number | string | null; unit: string | null; text: string | null;
  details: Record<string, unknown>; measured_at: string | null; session_key: string | null; source: string;
  superseded_by_id: number | null; clarification: string | null; scale_version: string | null; created_at: string;
}

export interface CompanionDocument {
  id: number; kind: string; title: string; version: number; released_at: string | null; source: string; content?: string;
}

export interface HelpContact { kind: string; label: string; number: string; country: string }

/** Time travel (dev/staging only): the member's "today" can be moved for multi-day tests. */
export interface CompanionClock { time_travel: boolean; simulated: boolean; today: string; real_today: string }

export interface CompanionOverview {
  enabled: boolean;
  clock?: CompanionClock;
  provider: { key: string | null; available: boolean };
  entitlement: CompanionEntitlement;
  profile: CompanionProfile | null;
  today: {
    date: string; present_kinds: ObservationKind[]; state: DayState;
    pending_message_id: number | null; preview_message_id: number | null;
    draft: CompanionPlan | null; confirmed_plan: CompanionPlan | null; last_plan: CompanionPlan | null;
  };
  documents: CompanionDocument[];
  safety: { rules_version: string; reviewed: boolean; help_contacts: HelpContact[] };
  contact_email: string | null;
  scales: Record<string, string>;
  kinds: ObservationKind[];
}

export interface CompanionMetrics {
  start_weight_kg: number | string | null; start_weight_on: string | null;
  current_weight_kg: number | string | null; current_weight_on: string | null;
  weight_change_30d_kg: number | string | null; weight_change_90d_kg: number | string | null;
  sleep_hours_avg_30d: number | null; sleep_quality_avg_30d: number | null; wellbeing_avg_30d: number | null;
  heart_rate_avg_30d: number | null; blood_pressure_avg_30d: { systolic: number; diastolic: number } | null;
  exercise_days_per_week: number[]; checkin_days_30d: number; confirmed_plans: number;
  days_since_last_checkin: number | null; bmi: number | null;
}

export interface CompanionDay {
  date: string; observations: CompanionObservation[]; plan: CompanionPlan | null; draft: CompanionPlan | null;
}

export interface GuardianTokenInfo {
  status: 'pending' | 'confirmed' | 'declined' | 'expired' | 'revoked' | 'invalid';
  member_first_name?: string; guardian_name?: string; consent_version?: string;
  confirmed_at?: string | null; expires_at?: string | null;
}

/** The kinds of the quick form and their input shape. */
export const FORM_KINDS: ReadonlyArray<{ kind: ObservationKind; input: 'number' | 'bp' | 'scale' | 'text'; max?: number }> = [
  { kind: 'weight',         input: 'number' },
  { kind: 'blood_pressure', input: 'bp' },
  { kind: 'heart_rate',     input: 'number' },
  { kind: 'sleep_hours',    input: 'number' },
  { kind: 'sleep_quality',  input: 'scale', max: 10 },
  { kind: 'supplement',     input: 'text' },
  { kind: 'hydrogen_water', input: 'number' },
  { kind: 'food',           input: 'text' },
  { kind: 'drink',          input: 'text' },
  { kind: 'exercise',       input: 'text' },
  { kind: 'wellbeing',      input: 'scale', max: 10 },
  { kind: 'mood',           input: 'scale', max: 5 },
  { kind: 'energy',         input: 'scale', max: 5 },
  { kind: 'stress',         input: 'scale', max: 5 },
  { kind: 'reflection',     input: 'text' },
];

export function newClientRef(): string {
  return (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);
}

@Injectable({ providedIn: 'root' })
export class HealthCompanionService {
  private http = inject(HttpClient);
  private readonly base = '/health_companion';

  overview(): Observable<CompanionOverview> { return this.http.get<CompanionOverview>(this.base); }

  profile(): Observable<{ profile: CompanionProfile }> { return this.http.get<{ profile: CompanionProfile }>(`${this.base}/profile`); }

  updateProfile(fields: Partial<CompanionProfile>, onboardingStep?: string): Observable<{ profile: CompanionProfile }> {
    return this.http.put<{ profile: CompanionProfile }>(`${this.base}/profile`, { companion: fields, onboarding_step: onboardingStep });
  }

  ageCheck(dateOfBirth: string, timeZone: string, language: string): Observable<{ profile: CompanionProfile; entitlement: CompanionEntitlement }> {
    return this.http.post<{ profile: CompanionProfile; entitlement: CompanionEntitlement }>(`${this.base}/profile/age`,
      { date_of_birth: dateOfBirth, confirm: true, time_zone: timeZone, language });
  }

  confirmProfile(): Observable<{ profile: CompanionProfile }> {
    return this.http.post<{ profile: CompanionProfile }>(`${this.base}/profile/confirm`, {});
  }

  /** Dev/staging only: move the member's "today" (ISO date) or reset it (null). */
  setClock(date: string | null): Observable<{ clock: CompanionClock; profile: CompanionProfile }> {
    return this.http.post<{ clock: CompanionClock; profile: CompanionProfile }>(`${this.base}/profile/clock`,
      date ? { date } : { reset: true });
  }

  setAi(enabled: boolean): Observable<{ profile: CompanionProfile }> {
    return this.http.post<{ profile: CompanionProfile }>(`${this.base}/profile/ai`, { enabled });
  }

  requestGuardian(name: string, email: string, relation: string): Observable<{ guardian: CompanionGuardian }> {
    return this.http.post<{ guardian: CompanionGuardian }>(`${this.base}/guardian`, { name, email, relation });
  }

  revokeGuardian(): Observable<{ guardian: null }> { return this.http.delete<{ guardian: null }>(`${this.base}/guardian`); }

  messages(before?: number): Observable<{ messages: CompanionMessage[]; has_more: boolean }> {
    let params = new HttpParams();
    if (before) params = params.set('before', String(before));
    return this.http.get<{ messages: CompanionMessage[]; has_more: boolean }>(`${this.base}/messages`, { params });
  }

  send(text: string, clientRef: string): Observable<{ messages: CompanionMessage[] }> {
    return this.http.post<{ messages: CompanionMessage[] }>(`${this.base}/messages`, { companion: { text, client_ref: clientRef } });
  }

  retry(messageId: number): Observable<{ message: CompanionMessage }> {
    return this.http.post<{ message: CompanionMessage }>(`${this.base}/messages/${messageId}/retry`, {});
  }

  confirmPreview(messageId: number, items: PreviewItem[]): Observable<{ observations: CompanionObservation[]; message: CompanionMessage; follow_up: 'plan' | null }> {
    return this.http.post<{ observations: CompanionObservation[]; message: CompanionMessage; follow_up: 'plan' | null }>(
      `${this.base}/messages/${messageId}/preview`, { companion: { items } });
  }

  discardPreview(messageId: number): Observable<{ message: CompanionMessage }> {
    return this.http.post<{ message: CompanionMessage }>(`${this.base}/messages/${messageId}/discard_preview`, {});
  }

  addObservations(items: PreviewItem[], source: 'form' | 'chip' = 'form', sessionKey?: string): Observable<{ observations: CompanionObservation[] }> {
    return this.http.post<{ observations: CompanionObservation[] }>(`${this.base}/observations`,
      { companion: { items, source, session_key: sessionKey } });
  }

  correct(observationId: number, change: { value?: number | null; value2?: number | null; text?: string | null; state?: ObservationState }):
    Observable<{ observation: CompanionObservation; superseded_id: number }> {
    return this.http.post<{ observation: CompanionObservation; superseded_id: number }>(
      `${this.base}/observations/${observationId}/correct`, { companion: change });
  }

  days(from?: string, to?: string): Observable<{ from: string; to: string; days: CompanionDay[]; metrics: CompanionMetrics }> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return this.http.get<{ from: string; to: string; days: CompanionDay[]; metrics: CompanionMetrics }>(`${this.base}/days`, { params });
  }

  propose(changeText?: string): Observable<{ messages: CompanionMessage[] }> {
    return this.http.post<{ messages: CompanionMessage[] }>(`${this.base}/plans/propose`, { companion: { change_text: changeText ?? null } });
  }

  confirmPlan(planId: number): Observable<{ plan: CompanionPlan }> {
    return this.http.post<{ plan: CompanionPlan }>(`${this.base}/plans/${planId}/confirm`, {});
  }

  declinePlan(planId: number): Observable<{ plan: CompanionPlan }> {
    return this.http.post<{ plan: CompanionPlan }>(`${this.base}/plans/${planId}/decline`, {});
  }

  reviewPlan(planId: number, done: number[], notes: string): Observable<{ plan: CompanionPlan }> {
    return this.http.post<{ plan: CompanionPlan }>(`${this.base}/plans/${planId}/review`, { companion: { done, notes } });
  }

  documents(withContent = false): Observable<{ documents: CompanionDocument[] }> {
    return this.http.get<{ documents: CompanionDocument[] }>(`${this.base}/documents`, { params: withContent ? { content: 1 } : {} });
  }

  export(format: 'json' | 'csv'): Observable<Blob> {
    return this.http.get(`${this.base}/export`, { params: { format }, responseType: 'blob' });
  }

  eraseAll(): Observable<{ deleted: number }> {
    return this.http.delete<{ deleted: number }>(this.base, { body: { companion: { confirm: 'DELETE' } } });
  }

  // --- public guardian link (no login) ---------------------------------------------

  guardianInfo(token: string): Observable<GuardianTokenInfo> {
    return this.http.get<GuardianTokenInfo>(`${this.base}/guardian/${encodeURIComponent(token)}`);
  }

  guardianAct(token: string, action: 'confirm' | 'decline' | 'revoke'): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/guardian/${encodeURIComponent(token)}/${action}`, {});
  }
}
